import { applyLevelCompletion } from "../model/ProgressionSystem";
import { LocalStorageSaveAdapter, SaveManager, type SaveAdapter } from "../model/SaveManager";
import type { LevelState } from "../model/LevelState";
import type { ChapterData } from "../types/chapter";
import type { LevelData } from "../types/level";
import type { GameSave } from "../types/save";
import {
  getCanonicalRawAssetBundle,
  loadValidatedAssetsFromRaw,
  type RawAssetBundle,
  type ValidatedAssets,
} from "./AssetLoader";

const SAVE_STORAGE_KEY = "signal-game-save";

export interface RuntimeSessionOptions {
  readonly rawAssets?: RawAssetBundle;
  readonly initialSave?: GameSave;
  readonly saveAdapter?: SaveAdapter;
}

export class RuntimeSession {
  private readonly saveManager: SaveManager;
  private readonly baseRawAssets: RawAssetBundle;
  private currentSave: GameSave | null;

  public constructor(options: RuntimeSessionOptions = {}) {
    this.saveManager = new SaveManager(options.saveAdapter ?? new LocalStorageSaveAdapter(SAVE_STORAGE_KEY));
    this.baseRawAssets = options.rawAssets ?? getCanonicalRawAssetBundle();
    this.currentSave = options.initialSave ?? null;
  }

  public async initialize(): Promise<void> {
    if (this.currentSave !== null) {
      await this.saveManager.save(this.currentSave);
    }

    this.currentSave = await this.saveManager.load();

    // Validate the configured runtime asset source during bootstrap so invalid
    // injected fixtures fail before Phaser creates a canvas.
    void this.getAssets();
  }

  public getCurrentSave(): GameSave {
    if (this.currentSave === null) {
      throw new Error("RuntimeSession has not been initialized.");
    }

    return this.currentSave;
  }

  public getAssets(): ValidatedAssets {
    return loadValidatedAssetsFromRaw(this.createRawBundleWithCurrentSave());
  }

  public async applyLevelCompletion(levelData: LevelData, levelState: LevelState): Promise<GameSave> {
    const chapter = this.getChapterForLevel(levelData.id);
    const collectedIds = [...new Set(levelState.collectibles.local)];
    const collectedSet = new Set(collectedIds);
    const groupCounts = new Map<string, { total: number; collected: number }>();
    for (const c of levelData.collectibles) {
      if (!c.group || c.group.trim() === "") continue;
      const existing = groupCounts.get(c.group) ?? { total: 0, collected: 0 };
      existing.total += 1;
      if (collectedSet.has(c.id)) {
        existing.collected += 1;
      }
      groupCounts.set(c.group, existing);
    }
    const completedGroups: string[] = [];
    for (const [group, counts] of groupCounts) {
      if (counts.collected === counts.total) {
        completedGroups.push(group);
      }
    }

    const triggeredBreadcrumbIds = [...levelState.breadcrumbs.local];

    const update = applyLevelCompletion(
      this.getCurrentSave(),
      {
        levelId: levelData.id,
        chapterId: chapter.id,
        cleared: levelState.completion.cleared,
        perfected: levelState.completion.perfected,
        collectedIds,
        completedGroups,
        triggeredBreadcrumbIds,
      },
      chapter,
    );

    await this.saveManager.save(update.save);
    this.currentSave = update.save;

    return this.getCurrentSave();
  }

  public async updateSave(updatedSave: GameSave): Promise<void> {
    await this.saveManager.save(updatedSave);
    this.currentSave = updatedSave;
  }

  public async clearSave(): Promise<void> {
    await this.saveManager.clear();
    this.currentSave = await this.saveManager.load();
  }

  private createRawBundleWithCurrentSave(): RawAssetBundle {
    return {
      symbols: this.baseRawAssets.symbols,
      chapters: { ...this.baseRawAssets.chapters },
      levels: { ...this.baseRawAssets.levels },
      save: this.getCurrentSave(),
    };
  }

  private getChapterForLevel(levelId: string): ChapterData {
    const assets = this.getAssets();
    const chapter = Object.values(assets.chapters).find((entry) => entry.levels.includes(levelId));

    if (chapter === undefined) {
      throw new Error(`Chapter for level ${levelId} not found in validated assets.`);
    }

    return chapter;
  }
}

let activeRuntimeSession: RuntimeSession | null = null;

export async function configureRuntimeSession(
  options: RuntimeSessionOptions = {},
): Promise<RuntimeSession> {
  const session = new RuntimeSession(options);
  await session.initialize();
  activeRuntimeSession = session;
  return session;
}

export function getRuntimeSession(): RuntimeSession {
  if (activeRuntimeSession === null) {
    throw new Error("RuntimeSession has not been configured.");
  }

  return activeRuntimeSession;
}
